import { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useSelector } from 'react-redux';
import { authSelector } from 'store/auth';
import Image from 'next/image';
import styles from 'styles/modules/PostAlert.module.scss';
import Input from './Input';
import Typo from './Typo';
import Post from './Post';
import PostHead from './PostHead';
import PostBody from './PostBody';
import SocialShare from './SocialShare';
import SlugField from './Alert/SlugField';
import TemplateFields, { ALERT_EDITOR_CONFIG } from './Alert/TemplateFields';
import safeHtml from 'utils/safe-html';
import {
    sourcePageHref,
    sourcePageRel,
    sourcePageText,
} from 'utils/source-page';
import SourcePageFields from './Alert/SourcePageFields';
import ResearchSponsorFields from './Alert/ResearchSponsorFields';
import sourceStyles from 'styles/modules/newsAlert/SourcePage.module.scss';
import newsroomStyles from 'styles/modules/newsAlert/Newsroom.module.scss';

const Editor = dynamic(() => import('./Editor'), {
    ssr: false,
});

const PostAlert = ({
    title,
    body,
    edit,
    // Remounts the editors when the form is refilled; they read their value only on mount.
    editorKey,
    setData,
    deadline,
    author,
    // h2 when a published story above the alert holds the page's h1.
    titleVariant = 'h1',
    // Admins only: the alert's address, editable alongside the title.
    isAdmin = false,
    slug,
    currentSlug,
    alertId,
    source,
    setSource,
    // { intro, questions } on alerts written with the template; null on older ones.
    template,
    storedIntro,
    // A sold research sponsorship's credit, which the box promises at the top of the alert.
    sponsorCredit,
    // The sponsorship price, which the spec wants directly under the headline.
    sponsorStrip,
    // Admins only: the story sponsorship draft, null until its saved offer has loaded.
    research,
    researchRow,
    setResearch,
    onResearchRowChange,
    researchError,
}) => {
    // Read after mount: the server has no location, so rendering it earlier breaks hydration.
    const [shareUrl, setShareUrl] = useState('');
    useEffect(() => setShareUrl(location.href), [currentSlug]);

    let titleElem;
    let postBody;

    const isFTEditorByRole = (author) => {
        if (!author?.roles || !Array.isArray(author.roles)) return false;
        return author.roles.some((role) => role.name === 'FT_EDITOR');
    };

    // FT Adviser branding names the publication an anonymised journalist writes for.
    const isCreatedByFTEditor =
        !author?.isAnonymised &&
        (author?.isFTEditor || isFTEditorByRole(author));

    const { isAuthenticated, company } = useSelector(authSelector);

    const authorView = author !== false && (
        <div className={['mb-l', styles.title].join(' ')}>
            <img src={'.'} alt={''} />
            <Typo variant={'p'} className={['mb-s', styles.author].join(' ')}>
                {!author.isAnonymised && 'Journalist: '}
                {author.name}
                {author.publication ? ', ' + author.publication : null}
                {author.isAnonymised && (
                    <span
                        className={styles.anonymisedInfo}
                        tabIndex={0}
                        aria-label="Why is the journalist hidden?"
                        data-nosnippet
                    >
                        i
                        <span className={styles.anonymisedPopover}>
                            <span>
                                The journalist&apos;s name and outlet are shown
                                to Plus and Pro members.{' '}
                                {isAuthenticated ? (
                                    <Link href={`/${company}/subscription`}>
                                        <a>Upgrade</a>
                                    </Link>
                                ) : (
                                    <Link href="/login">
                                        <a>Log in</a>
                                    </Link>
                                )}
                            </span>
                        </span>
                    </span>
                )}
            </Typo>
        </div>
    );

    // A journalist's source page is on their outlet's own site.
    const sourceHref =
        !edit &&
        !author?.isAnonymised &&
        sourcePageHref(source?.sourceUrl, currentSlug);
    // Rendered as JSX: safeHtml would rewrite the rel of a link inside the body.
    const sourceLine = sourceHref && source.sourceBy && (
        <p className={sourceStyles.source}>
            Based on analysis by {source.sourceBy}:{' '}
            <a
                href={sourceHref}
                target="_blank"
                rel={sourcePageRel(source.isSponsored)}
            >
                {sourcePageText(source.sourceLabel, source.sourceUrl)}
            </a>
        </p>
    );

    if (edit) {
        titleElem = (
            <>
                <Input
                    value={title}
                    onChange={(e) => setData.setTitle(e.target.value)}
                    classes={styles.input}
                />
                {isAdmin && setData.setSlug && (
                    <SlugField
                        value={slug}
                        onChange={setData.setSlug}
                        onStatus={setData.setSlugStatus}
                        alertId={alertId}
                        currentSlug={currentSlug}
                    />
                )}
            </>
        );

        postBody = (
            <>
                {template ? (
                    <TemplateFields
                        key={editorKey}
                        intro={template.intro}
                        questions={template.questions}
                        onChange={setData.setTemplate}
                        storedIntro={storedIntro}
                    />
                ) : (
                    <Editor
                        key={editorKey}
                        data={body}
                        setData={setData.setBody}
                        height={'400px'}
                        config={ALERT_EDITOR_CONFIG}
                    />
                )}
                {setSource && (
                    <div className="mt-l">
                        <SourcePageFields
                            {...source}
                            canSponsor={isAdmin}
                            onChange={(field, value) =>
                                setSource({ ...source, [field]: value })
                            }
                        />
                    </div>
                )}
                {isAdmin && researchError && (
                    <p className={newsroomStyles.error} role="alert">
                        {researchError}
                    </p>
                )}
                {isAdmin && research && (
                    <ResearchSponsorFields
                        alertId={alertId}
                        draft={research}
                        row={researchRow}
                        onChange={(field, value) =>
                            setResearch({ ...research, [field]: value })
                        }
                        onRowChange={onResearchRowChange}
                    />
                )}
            </>
        );
    } else {
        titleElem = (
            <>
                <Typo
                    variant={titleVariant}
                    className={[
                        author === false ? 'mb-s' : '',
                        styles.title,
                    ].join(' ')}
                >
                    {title}
                </Typo>
                {authorView}
            </>
        );

        postBody = <div dangerouslySetInnerHTML={{ __html: safeHtml(body) }} />;
    }

    const social = !edit && shareUrl && (
        <div className={styles.socialIcons}>
            <span>Share</span>
            <SocialShare
                platform={'facebook'}
                url={shareUrl}
                fill="var(--color-blue)"
            />
            <SocialShare
                platform={'twitter'}
                url={shareUrl}
                fill="var(--color-blue)"
            />
            <SocialShare
                platform={'linkedin'}
                url={shareUrl}
                fill="var(--color-blue)"
            />
        </div>
    );

    return (
        <Post>
            <PostHead>
                <header>
                    {isCreatedByFTEditor && (
                        <div className={styles.ftBannerContainer}>
                            <Image
                                src="/img/news-story/FT-banner.webp"
                                alt="FT Editor Banner"
                                width={800}
                                height={300}
                                className={styles.ftBanner}
                            />
                        </div>
                    )}
                    {titleElem}
                    {!edit && sponsorStrip}
                    {sourceLine}
                    <div className={styles.metaWrapper}>
                        {deadline}
                        {social}
                    </div>
                    {!edit && sponsorCredit}
                </header>
            </PostHead>

            <PostBody>{postBody}</PostBody>

            {/*<div className={styles.disclaimer}>*/}
            {/*    Publishers: if you use any, or all, of the responses in this*/}
            {/*    News Alert, please credit{' '}*/}
            {/*    <strong>*/}
            {/*        <a*/}
            {/*            href={'https://newspage.media'}*/}
            {/*            target={'_blank'}*/}
            {/*            rel={'noreferrer'}*/}
            {/*        >*/}
            {/*            Newspage*/}
            {/*        </a>*/}
            {/*    </strong>*/}
            {/*    , e.g. "Speaking to the Newspage news agency, XXXX said...".{' '}*/}
            {/*    <br />*/}
            {/*    <br />*/}
            {/*    For ease, all, or individual quotes, can be copied.*/}
            {/*</div>*/}
        </Post>
    );
};

export default PostAlert;
